// Phase 7 R3 — atomic receiving via record_parts_receipt RPC.
// Updates PO line/header status, mirrors parts_lines.procurement_status,
// writes qty_received (and optional cost_corrected) audit events.
// Pricing fields on parts_lines are NEVER mutated by this path.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface RecordPartsReceiptLineInput {
  purchase_order_line_id: string;
  quantity_received: number;
  unit_cost_actual?: number | null;
  core_charge_actual?: number | null;
  notes?: string | null;
}

export interface RecordPartsReceiptInput {
  shopId: string;
  purchaseOrderId: string;
  packing_slip_ref?: string | null;
  notes?: string | null;
  received_at?: string | null; // ISO; defaults server-side to now()
  lines: RecordPartsReceiptLineInput[];
}

export function useRecordPartsReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RecordPartsReceiptInput): Promise<string> => {
      if (!input.lines.length) throw new Error('Add at least one receipt line');

      const { data, error } = await (supabase as any).rpc('record_parts_receipt', {
        p_shop_id: input.shopId,
        p_purchase_order_id: input.purchaseOrderId,
        p_receipt: {
          packing_slip_ref: input.packing_slip_ref ?? null,
          notes: input.notes ?? null,
          received_at: input.received_at ?? null,
          lines: input.lines.map((l) => ({
            purchase_order_line_id: l.purchase_order_line_id,
            quantity_received: l.quantity_received,
            unit_cost_actual: l.unit_cost_actual ?? null,
            core_charge_actual: l.core_charge_actual ?? 0,
            notes: l.notes ?? null,
          })),
        },
      });

      if (error) throw error;
      return data as string;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['purchase-order', vars.purchaseOrderId] });
      qc.invalidateQueries({ queryKey: ['purchase-orders'] });
      qc.invalidateQueries({ queryKey: ['parts-lines'] });
      qc.invalidateQueries({ queryKey: ['ticket-po-linked-parts-lines'] });
      qc.invalidateQueries({ queryKey: ['po-receipts', vars.purchaseOrderId] });
    },
  });
}
